import type { ComponentPropsWithoutRef } from "react";

import {
  CoilType as CoilTypeType,
  CommandMan,
  CommandType,
  State,
  ValveCommand,
  type Coil as CoilType,
} from "@eisox/api/core-api";
import { Circle, cn } from "@eisox/design-system";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  HeatIcon,
  HydraulicChangeoverIcon,
  SnowFlackIcon,
  ThreeWayValveIcon,
  TwoWayValveIcon,
  ZapIcon,
  type IconProps,
} from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { createLink, type LinkComponent } from "@tanstack/react-router";

import WarningIcon from "~/assets/boilerroomWarning.svg?react";
import Commutator from "~/assets/commutator.svg?react";
import CoolingUnit from "~/assets/coolingUnit.svg?react";
import { BoilerRoom } from "~/components/boiler-room";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { getActuatorState, isAutoManualOrForcing } from "../helpers";
import { Position, type Component } from "../types";
import { Card } from "./card";

const CreatedLinkCard = createLink(Card);

const LinkCard: LinkComponent<typeof Card> = props => <CreatedLinkCard {...props} />;

type HydraulicCoilType =
  | typeof CoilTypeType.HYDRAULIC_HEATING
  | typeof CoilTypeType.HYDRAULIC_COOLING
  | typeof CoilTypeType.HYDRAULIC_CHANGEOVER;

interface HydraulicCoilStyle {
  pipes: string;
  threeWayValveBranch: string;
  coilColor: string;
  Icon: React.ComponentType<IconProps>;
  label: () => string;
}

const HYDRAULIC_COIL_STYLE: Record<HydraulicCoilType, HydraulicCoilStyle> = {
  [CoilTypeType.HYDRAULIC_HEATING]: {
    pipes: "before:bg-water-cold after:bg-water-hot",
    threeWayValveBranch: "bg-water-hot",
    coilColor: colors.water.hot,
    Icon: HeatIcon,
    label: m.air_handler_unit_coil_hydraulic_heating,
  },
  [CoilTypeType.HYDRAULIC_COOLING]: {
    pipes: "before:bg-water-colder after:bg-water-cold",
    threeWayValveBranch: "bg-water-cold",
    coilColor: colors.water.cold,
    Icon: SnowFlackIcon,
    label: m.air_handler_unit_coil_hydraulic_cooling,
  },
  [CoilTypeType.HYDRAULIC_CHANGEOVER]: {
    pipes: "before:bg-primary-dark-gray after:bg-primary-dark-gray",
    threeWayValveBranch: "bg-primary-dark-gray",
    coilColor: colors.primary.darkGray,
    Icon: HydraulicChangeoverIcon,
    label: m.air_handler_unit_coil_hydraulic_changeover,
  },
};

interface CoilProps extends Omit<CoilType, "id">, Component, ComponentPropsWithoutRef<"div"> {}

const NAME = "Coil";

const Coil: React.FC<CoilProps> = ({
  state: stateProp,
  command,
  commandMan,
  commandAutomate,
  commandType,
  coilType,
  twoWayValve,
  threeWayValve,
  heatingNetworkId,
  defect = false,
  missmatchDefect = false,
  position = Position.TOP,
  ...props
}) => {
  const { building, heatingNetworks } = useBuildingContext(NAME);
  const { state, warning } = getActuatorState({ state: stateProp, commandMan, commandAutomate, defect });
  const autoManualOrForcing = isAutoManualOrForcing(commandMan, command);

  const valveManual = threeWayValve?.command === ValveCommand.MANUAL || twoWayValve?.command === ValveCommand.MANUAL;
  const valveOpening = threeWayValve?.opening ?? twoWayValve?.opening;
  const hydraulicStyle =
    coilType === CoilTypeType.ELECTRIC || coilType === CoilTypeType.CHILLER
      ? undefined
      : HYDRAULIC_COIL_STYLE[coilType];
  const boilerRoomId = heatingNetworkId
    ? heatingNetworks.find(network => network.id === heatingNetworkId)?.boilerRoomId
    : undefined;

  return (
    <div
      className={cn("px-5 flex w-fit flex-col items-center", position === Position.BOTTOM && "flex-col-reverse")}
      {...props}
      style={{ cursor: props.onClick ? "pointer" : "default" }}
    >
      {(coilType === CoilTypeType.ELECTRIC || coilType === CoilTypeType.CHILLER) && (
        <Card className={position === Position.TOP ? "mb-1.25" : "mt-1.25"} defect={defect || missmatchDefect}>
          {commandMan === CommandMan.MANUAL && <Commutator />}
          {commandType === CommandType.PERCENTAGE &&
            `${m.actuator_state({ state: autoManualOrForcing })} - ${state?.toFixed(1) ?? "--"} %`}
          {commandType === CommandType.ALL_OR_NOTHING &&
            `${m.actuator_state({ state: autoManualOrForcing })} - ${state !== null ? (state === State.ON ? m.on() : m.off()) : "--"}`}
          {warning && (
            <Tooltip content={warning}>
              <WarningIcon />
            </Tooltip>
          )}
        </Card>
      )}
      {hydraulicStyle && (
        <div
          data-testid="coil-pipes"
          className={cn(
            "h-50 w-9.25 relative flex flex-col items-center",
            "before:left-0 after:right-0 before:w-2 after:w-2 before:absolute before:-z-1 before:h-full before:content-[''] after:absolute after:-z-1 after:h-full after:content-['']",
            position === Position.TOP && "before:rounded-t-[90px] after:rounded-t-[90px]",
            position === Position.BOTTOM && "flex-col-reverse before:rounded-b-[90px] after:rounded-b-[90px]",
            hydraulicStyle.pipes,
          )}
        >
          {(twoWayValve !== undefined || threeWayValve !== undefined) && (
            <div className="absolute top-1/2 left-[-18.5px] flex -translate-y-1/2 items-center">
              <Circle.Root size={45} color="white" backgroundColor="darkBlue">
                {threeWayValve !== undefined ? (
                  <ThreeWayValveIcon />
                ) : (
                  <TwoWayValveIcon style={{ transform: "rotate(90deg)" }} />
                )}
                {threeWayValve !== undefined && (
                  <div className={cn("h-2 w-6.25 absolute left-1/2 -z-1", hydraulicStyle.threeWayValveBranch)} />
                )}
                <Card className="py-1.25 pr-3.75 pl-2.5 absolute top-1/2 right-[calc(100%-10px)] -z-1 -translate-y-1/2">
                  {`${valveManual ? `${m.forcing()}${threeWayValve !== undefined ? " - " : ""}` : ""} ${
                    valveOpening?.toFixed(1) ?? "-"
                  }%`}
                </Card>
              </Circle.Root>
            </div>
          )}
          {boilerRoomId ? (
            <LinkCard
              to="/buildings/$buildingId/boiler-rooms/$boilerRoomId"
              params={{ buildingId: building.id, boilerRoomId }}
              className="w-max"
              onClick={e => e.stopPropagation()}
            >
              <BoilerRoom size={40} backgroundColor="darkBlue" color="white" />
              {position === Position.TOP ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </LinkCard>
          ) : (
            <Card className="w-max">
              <BoilerRoom size={40} backgroundColor="darkBlue" color="white" />
              {position === Position.TOP ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </Card>
          )}
        </div>
      )}
      <div className="h-23 w-12 p-0.5 flex flex-col items-center justify-center rounded-[5px] border border-primary-dark-blue bg-primary-gray">
        {hydraulicStyle && (
          <>
            <div className="p-1.25 grid w-full flex-1 grid-flow-row grid-cols-3 place-items-center">
              {Array.from({ length: 12 }).map((_, i) => (
                <svg key={i} width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4.83301 1.125C0.833008 1.125 0.833008 8.125 4.83301 8.125"
                    stroke={hydraulicStyle.coilColor}
                    strokeWidth="2"
                    strokeLinecap="square"
                  />
                </svg>
              ))}
            </div>
            <hydraulicStyle.Icon color={hydraulicStyle.coilColor} role="img" aria-label={hydraulicStyle.label()} />
          </>
        )}
        {coilType === CoilTypeType.ELECTRIC && (
          <Circle.Root backgroundColor="darkBlue" color="white">
            <ZapIcon color="#FFC000" />
          </Circle.Root>
        )}
        {coilType === CoilTypeType.CHILLER && (
          <Circle.Root backgroundColor="darkBlue" color="white">
            <CoolingUnit />
          </Circle.Root>
        )}
      </div>
    </div>
  );
};

Coil.displayName = "Coil";

export { Coil };
export type { CoilProps };
